import './HowItWorksFlow.css';

interface FlowStep {
  title: string;
  description: string;
}

interface HowItWorksFlowProps {
  title?: string;
  steps?: FlowStep[];
  className?: string;
}

const DEFAULT_STEPS: FlowStep[] = [
  {
    title: 'Real-Time Data Connectivity',
    description:
      'Connects to supplier factory databases, continuously monitoring IQC, IPQC, OQC inspection data and process parameters',
  },
  {
    title: 'AI-Powered Anomaly Detection',
    description:
      'Dynamically compares new data against historical data, identifies anomalies using self-evolving rules — not static thresholds',
  },
  {
    title: 'Automatic Alert & Recommendations',
    description:
      'When critical process abnormalities are detected, automatically triggers alert notifications and generates actionable corrective action proposals',
  },
  {
    title: 'Self-Learning Knowledge Base',
    description:
      'Records every triggered alert and its resolution, continuously refining detection rules — the more it is used, the more precise and effective it becomes',
  },
];

/**
 * Animated "How It Works" pipeline. Steps light up sequentially while a data
 * pulse travels down the connector, then loops back to step one to echo the
 * self-learning feedback cycle. Pure CSS animation, themeable, reduced-motion
 * aware.
 */
export function HowItWorksFlow({
  title = 'How It Works',
  steps = DEFAULT_STEPS,
  className,
}: HowItWorksFlowProps) {
  const count = steps.length;
  // One full cycle highlights every step in turn before repeating.
  const cycle = count * 2.2;

  return (
    <div
      className={`hiw${className ? ` ${className}` : ''}`}
      style={{ ['--hiw-cycle' as string]: `${cycle}s`, ['--hiw-count' as string]: count }}
    >
      <h3 className="hiw__heading">
        <span className="hiw__heading-icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
            <path
              d="M21 12a9 9 0 1 1-3-6.7M21 4v4h-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {title}
      </h3>

      <div className="hiw__list">
        {/* Connector line with a flowing pulse traveling top -> bottom */}
        <div className="hiw__connector" aria-hidden>
          <span className="hiw__connector-flow" />
        </div>

        {steps.map((step, i) => (
          <div
            key={step.title}
            className="hiw__step"
            style={{ ['--hiw-i' as string]: i }}
          >
            <div className="hiw__badge" aria-hidden>
              <span className="hiw__badge-ring" />
              <span className="hiw__badge-num">{i + 1}</span>
            </div>
            <div className="hiw__body">
              <h4 className="hiw__step-title">{step.title}</h4>
              <p className="hiw__step-desc">{step.description}</p>
            </div>
            <span className="hiw__sweep" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}
